<template>
  <div>
    <v-dialog @keydown.esc="dialogOpen=false" v-model="dialogOpen" persistent max-width="816px">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            small
            color="transparent"
            depressed
            @click="closeDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="dialogTitle">{{ $t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.title') }}</v-card-title>
        <v-card-text class="d-flex flex-column justify-center">
          <div v-if="table.data.length">
            <v-data-table
              :loading-text="table.loadingText"
              :loading="table.loading"
              :headers="table.headers"
              :items="table.data"
              :options="table.options"
              hide-default-footer
              class="table"
            >
              <template v-slot:[`item._id`]="{ item }">
                <div>
                  <a @click="openPayOrderDetailsPanel(item)" class="link">{{ item._id }}</a>
                </div>
              </template>
              <template v-slot:[`item.date`]="{ item }">
                <div>
                  {{ $moment(item.date).format('DD/MM/YYYY') }}
                </div>
              </template>
              <template v-slot:[`item.paymentMethod`]="{ item }">
                <div>
                  {{ PAYORDER_PAYMENT_TYPES[item.paymentMethod] }}
                </div>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <div>
                  {{ STATUS_TYPES[item.status] }}
                </div>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <div class="text-right">{{ item.amount | toFix2 | numberWithComma | prefix('$') }}</div>
              </template>
              <template v-slot:[`item.provider`]="{ item }">
                <div>
                  {{ item.invoices[0] ? item.invoices[0].source : '-' }}
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div v-if="!item.related">
                  <v-btn fab x-small color="primary" @click="addRelatedPayOrder(item._id)">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <div v-else class="noInfo mt-2">
            {{ $t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.noInfo') }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <PayOrderDetails
      :isOpen.sync="payOrderDetailsPanelOpen"
      v-bind:selectedPayOrder="selectedPayOrder"
      @close="resetPayOrderDetails()"
    />
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import PayOrderDetails from '@/components/sidePanels/PayOrderDetails'

export default {
  name: 'RelatedPayOrdersViewer',
  props: {},
  emits: ['updateRelations', 'updateRelatedPayOrders'],
  components: {
    PayOrderDetails
  },
  data () {
    return {
      overlay: false,
      dialogOpen: false,
      table: {
        loadingText: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.loadingText'),
        loading: false,
        headers: [
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.id'), value: '_id', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.date'), value: 'date', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.paymentMethod'), value: 'paymentMethod', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.status'), value: 'status', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.amount'), value: 'amount', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.supplier'), value: 'provider', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedPayOrdersDialog.table.headers.actions'), value: 'actions', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: -1,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [],
          sortDesc: []
        }
      },
      selectedPayOrder: {},
      payOrderDetailsPanelOpen: false,
      STATUS_TYPES: {
        created: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.statusTypes.created'),
        pending_payment: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.statusTypes.pendingPayment'),
        paid: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.statusTypes.paid')
      },
      PAYORDER_PAYMENT_TYPES: {
        balance: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.paymentTypes.balance'),
        credit: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.paymentTypes.credit')
      },
      relationId: undefined,
      bankTransactionsTotal: undefined
    }
  },
  watch: {
    dialogOpen: function (val) {
      if (val) {
        this.loadPayOrders()
      }
    }
  },
  methods: {
    openDialog (relation) {
      this.relationId = relation._id
      this.bankTransactionsTotal = relation.bankTransactionsTotal
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
    },
    loadPayOrders: async function (params = null) {
      params = params || {
        amount: this.bankTransactionsTotal,
        // amount: sumBy(this.transactions, 'amount'),
        restaurant: this.$store.state.currentRestaurant.data._id,
        'status!': 'paid'
      }
      this.table.loading = true
      try {
        const response = await this.$api.get(`/v1/expenses/${this.$store.state.currentRestaurant.data._id}`, params)
        this.table.data = response
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    addRelatedPayOrder: async function (payOrderId) {
      const payload = {
        bankTransactions: [],
        orders: [ payOrderId ],
        transfers: [],
        updatedBy: this.$store.state.login.user._id
      }
      this.overlay = true
      try {
        await this.$api.put(`/v1/bank-reconciliation/${this.relationId}`, payload)
        // Reload payorders
        this.loadPayOrders()
        // Reload related payorders
        this.$emit('updateRelatedPayOrders')
        // Reload relations
        this.$emit('updateRelations')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    formatCurrency,
    openPayOrderDetailsPanel (item) {
      this.selectedPayOrder = item
      this.payOrderDetailsPanelOpen = true
    },
    resetPayOrderDetails () {
      this.selectedPayOrder = {}
    }
  }
}
</script>

<style lang="scss" scoped>
  .dialogTitle {
    display: flex;
    justify-content: center;
    padding-top: 0 !important;
    font-size: 22px !important;
    color: #01D28E;
  }

  .table {
    overflow-y: auto;
    max-height: 65vh;

    & tr {
      border: 1px solid #ACABAB;
      border-radius: 7px;

      &:first-child {
        border-left: 1px solid #ACABAB;
        border-top-left-radius: 7px;
        border-bottom-left-radius: 7px;
      }
    }

    & td {
      border-top: 1px solid #ACABAB;
      border-bottom: 1px solid #ACABAB;

      &:last-child {
        border-right: 1px solid #ACABAB;
        border-top-right-radius: 7px;
        border-bottom-right-radius: 7px;
      }
    }

    & thead tr:last-child th {
      border-bottom: none !important;
    }
  }

  .sumText {
    margin-top: 23px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #3F3F3F;
  }

  .noInfo {
    border: 1px solid #ACABAB;
    border-radius: 7px;
    padding-top: 22px;
    padding-bottom: 22px;
    text-align: center;
    line-height: 19px;
    color: #ACABAB;
  }
</style>
